import { escapeHtml } from './utils.js';

export function editorText(editor) {
  if (!editor) return '';
  return (editor.innerText || editor.textContent || '').replace(/\u00a0/g, ' ').replace(/\n$/, '');
}

export function setEditorText(editor, text = '') {
  editor.innerHTML = String(text).split('\n').map(escapeHtml).join('<br>');
}

export function setEditorHtml(editor, html = '') {
  editor.innerHTML = String(html || '');
  editor.querySelectorAll('img[src^="cid:boi-"]').forEach((image) => {
    const id = image.getAttribute('src').slice('cid:boi-'.length);
    image.src = editor.closest('[data-shared-composer]') ? `/api/v1/composer-assets/${id}?inline=1` : `/api/v1/attachments/${id}?inline=1`;
  });
}

export function editorHtml(editor) {
  if (!editor) return '';
  const clone = editor.cloneNode(true);
  clone.querySelectorAll('img[src^="/api/v1/"]').forEach((image) => {
    const match = /^\/api\/v1\/(?:attachments|composer-assets)\/([a-f0-9]{24})\?inline=1$/i.exec(image.getAttribute('src') || '');
    if (match) image.setAttribute('src', `cid:boi-${match[1]}`);
  });
  return clone.innerHTML;
}

function textNodes(editor) {
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

export function selectionOffsets(editor) {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editor.contains(selection.anchorNode)) return null;
  const range = selection.getRangeAt(0);
  const lengthTo = (node, offset) => {
    const before = document.createRange(); before.selectNodeContents(editor); before.setEnd(node, offset);
    return before.toString().length;
  };
  return { start: lengthTo(range.startContainer, range.startOffset), end: lengthTo(range.endContainer, range.endOffset) };
}

export function restoreSelection(editor, offsets) {
  if (!offsets) return;
  const nodes = textNodes(editor);
  const point = (position) => {
    let left = position;
    for (const node of nodes) { if (left <= node.length) return [node, left]; left -= node.length; }
    return nodes.length ? [nodes.at(-1), nodes.at(-1).length] : [editor, editor.childNodes.length];
  };
  const range = document.createRange();
  const [startNode, startOffset] = point(offsets.start);
  const [endNode, endOffset] = point(offsets.end);
  range.setStart(startNode, startOffset); range.setEnd(endNode, endOffset);
  editor.focus({ preventScroll: true });
  const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
}

export function insertHtml(editor, html) {
  editor.focus({ preventScroll: true });
  const selection = window.getSelection();
  const range = selection?.rangeCount && editor.contains(selection.anchorNode) ? selection.getRangeAt(0) : null;
  const target = range || document.createRange();
  if (!range) { target.selectNodeContents(editor); target.collapse(false); }
  target.deleteContents();
  const fragment = target.createContextualFragment(html);
  const last = fragment.lastChild;
  target.insertNode(fragment);
  if (last) { target.setStartAfter(last); target.collapse(true); selection.removeAllRanges(); selection.addRange(target); }
  editor.dispatchEvent(new Event('input', { bubbles: true }));
}

export function installRichEditor(editor) {
  if (!editor || Object.hasOwn(editor, 'value')) return;
  Object.defineProperty(editor, 'value', { configurable: true, get: () => editorText(editor), set: (text) => setEditorText(editor, text) });
  editor.addEventListener('paste', (event) => {
    event.preventDefault();
    insertHtml(editor, escapeHtml(event.clipboardData?.getData('text/plain') || '').replace(/\r?\n/g, '<br>'));
  });
}

export function translatableSegments(editor) {
  return textNodes(editor).filter((node) => node.textContent.trim()).map((node) => ({ node, text: node.textContent }));
}
